import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import CognitoService from '../services/cognitoService';

function AuthCallback() {
  const navigate = useNavigate();
  const { loginWithCognito } = useAuth();
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(true);
  const [status, setStatus] = useState('Initializing...');
  const hasProcessed = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent double processing
      if (hasProcessed.current) {
        return;
      }
      hasProcessed.current = true;

      try {
        setStatus('Checking authentication response...');
        
        // Check if this is a Cognito callback
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const error = urlParams.get('error');

        if (error) {
          setError(`Authentication failed: ${error}`);
          setProcessing(false);
          return;
        }

        if (!code) {
          setError('Authentication failed - no authorization code received');
          setProcessing(false);
          return;
        }

        setStatus('Processing Cognito authentication...');
        
        // Get user data from Cognito
        const userData = await CognitoService.handleAuthCallback();
        
        setStatus('Completing sign-in...');
        
        // Login through your existing auth context
        await loginWithCognito(userData);
        
        setStatus('Redirecting to dashboard...');
        
        // Small delay to show success message
        setTimeout(() => {
          navigate('/');
        }, 1000);
        
      } catch (error) {
        
        // More specific error messages
        let errorMessage = 'Authentication failed. Please try again.';
        if (error.message.includes('Token exchange failed')) {
          errorMessage = 'Failed to exchange authorization code. Please try signing in again.';
        } else if (error.message.includes('Failed to process authentication callback')) {
          errorMessage = 'Authentication callback processing failed. Please try again.';
        } else if (error.message.includes('Network Error') || error.code === 'ECONNREFUSED') {
          errorMessage = 'Unable to connect to authentication server. Please check your connection and try again.';
        }
        
        setError(errorMessage);
        setProcessing(false);
      }
    };

    handleCallback();
  }, [navigate, loginWithCognito]);

  if (processing) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        sx={{ p: 3 }}
      >
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Completing your sign-in...
        </Typography>
        <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 1 }}>
          {status}
        </Typography>
        <Typography variant="caption" color="textSecondary" align="center">
          Please wait while we securely authenticate you with your enterprise account.
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        sx={{ p: 3 }}
      >
        <Alert severity="error" sx={{ mb: 2, maxWidth: 500 }}>
          {error}
        </Alert>
        <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 2 }}>
          You can try signing in again or contact support if the problem persists.
        </Typography>
        <Typography 
          variant="body2" 
          color="primary" 
          align="center" 
          sx={{ cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => navigate('/login')}
        >
          Return to Login
        </Typography>
      </Box>
    );
  }

  return null;
}

export default AuthCallback;
